import { Building2, KeyRound, RotateCcw } from 'lucide-react';
import { Button, Drawer } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { UserAssignments } from '../../rbac/ui/UserAssignments.js';
import type { ScopeOption } from '../../rbac/ui/ScopePicker.js';
import type { StaffDto } from '../api/staff.api.js';

/**
 * XODIM PANELI — bir odam haqidagi hamma narsa bitta joyda.
 *
 * Ilgari har qatorda to'rtta havola turardi: «Ish joyi», «Rollar»,
 * «Ruxsatlar», «Parolni tiklash». Uchtasi quloqqa bir xil narsa bo'lib
 * eshitilardi, aslida esa uchta boshqa narsa edi, va ular jadvalning
 * yarmini egallab turardi. Qaysi biri asosiy ekani ham ko'rinmasdi.
 *
 * Eng muhimi: ISH JOYI bilan KIRISH HUQUQI endi yonma-yon turadi. Ular
 * ikki xil narsani hal qiladi — ish joyi xodim YOZGAN hujjat qaysi
 * korxonaniki bo'lishini, doira esa uning NIMANI KO'RISHINI — va ikkalasi
 * ikki xil oynada bo'lganida bu farq hech qayerda ko'rinmasdi. Prodda
 * aynan shu chalkashlik xodimni o'zi yozgan talabnomadan ayirgan edi.
 */

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-text-muted shrink-0 text-xs">{label}</dt>
    <dd className="text-text min-w-0 truncate text-sm">{value}</dd>
  </div>
);

export interface StaffPanelProps {
  staff: StaffDto | null;
  onClose: () => void;
  scopeOptions: ScopeOption[];
  /** Ish joyi oynasini ochadi. `null` — ruxsat yo'q. */
  onEditWorkplace: ((staff: StaffDto) => void) | null;
  onShowPermissions: (staff: StaffDto) => void;
  /** Parolni tiklash oynasini ochadi. `null` — ruxsat yo'q. */
  onResetPassword: ((staff: StaffDto) => void) | null;
}

export const StaffPanel = ({
  staff,
  onClose,
  scopeOptions,
  onEditWorkplace,
  onShowPermissions,
  onResetPassword,
}: StaffPanelProps) => (
  <Drawer
    open={staff !== null}
    onClose={onClose}
    title={staff?.fullName ?? ''}
    description={staff === null ? undefined : staff.username}
    className="w-[min(34rem,100vw)]"
    footer={
      <div className="flex flex-wrap items-center gap-2">
        {staff === null ? null : (
          <>
            <Button variant="ghost" size="sm" onClick={() => onShowPermissions(staff)}>
              <KeyRound size={15} aria-hidden />
              {t('staff.actionPermissions')}
            </Button>
            {onResetPassword === null ? null : (
              <Button variant="ghost" size="sm" onClick={() => onResetPassword(staff)}>
                <RotateCcw size={15} aria-hidden />
                {t('staff.actionReset')}
              </Button>
            )}
          </>
        )}
      </div>
    }
  >
    {staff === null ? null : (
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-text text-sm font-semibold">{t('staff.panelWorkplace')}</h3>
            {onEditWorkplace === null ? null : (
              <Button variant="ghost" size="sm" onClick={() => onEditWorkplace(staff)}>
                <Building2 size={15} aria-hidden />
                {t('staff.panelChange')}
              </Button>
            )}
          </div>

          <dl className="border-border bg-surface-2 flex flex-col gap-1.5 rounded-[var(--radius-sm)] border px-3 py-2.5">
            <Row
              label={t('staff.colOrgUnit')}
              value={staff.orgUnitName ?? t('staff.noEmployment')}
            />
            <Row
              label={t('staff.fieldSubdivision')}
              value={staff.subdivisionName ?? t('staff.noSubdivision')}
            />
            <Row label={t('staff.colPosition')} value={staff.positionName ?? '—'} />
          </dl>

          {/* Farqni AYNAN shu yerda aytish kerak: pastda doira turibdi va
              ikkalasi bir xil narsa bo'lib ko'rinadi. */}
          <p className="text-text-muted text-xs text-pretty">{t('staff.panelWorkplaceHint')}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-text text-sm font-semibold">{t('staff.panelAccess')}</h3>
          <UserAssignments userId={staff.id} scopeOptions={scopeOptions} nested />
        </section>
      </div>
    )}
  </Drawer>
);
